import {
  square,
  sqrt,
  ceilToIntegers,
  roundToTenths,
  ceilToHundredths
} from '../math';

export type roof = {
  [key: string]: (...args: [{ [key: string]: number }]) => number,
};

// 1 Gambrel Мансардная кровля
export const gambrel: roof = {
  /*
  L_kr Длина карниза
  B_kr1 Ширина заложения 1
  B_kr2 Ширина заложения 2
  H_p1 Высота подъема 1
  H_p2 Высота подъема 2
  sheet_width Рабочая ширина листа
  area_coeff Коэффициент площади
  */
  S_kr: ({ L_kr, B_kr1, B_kr2, H_p1, H_p2, sheet_width, area_coeff }) => {
    const sw = sheet_width || 1;
    const ac = area_coeff || 1;
    const sheets = sheet_width ? ceilToIntegers(L_kr / sw) : ceilToIntegers(L_kr);
    return roundToTenths(sheets * (B_kr2 + H_p1 + H_p2 + B_kr1) * ac);
  },
  L_kon: ({ L_kr }) => 3 * L_kr,
  N_kon: ({ L_kr }) => ceilToIntegers(L_kr / 1.95),
  N_karn: ({ L_kr }) => ceilToIntegers(4 * L_kr / 1.95),
  N_tor: ({ B_kr1, B_kr2, H_p1, H_p2 }) =>
    ceilToIntegers(2 * (B_kr2 + H_p1 + H_p2 + B_kr1) / 1.95),
};

// 2 Hip Вальмовая кровля
export const hip: roof = {
  /*
  L_kr Ширина ската 1 (L) — длина карниза по длинной стороне
  B_kr Ширина ската 2 (M) — длина карниза по короткой стороне
  H_p1 Длина ската 1 (a) — длина ската трапециевидной грани
  H_p2 Длина ската 2 (a2) — длина ската треугольной грани
  L_k  Длина конька (k)
  area_coeff Коэффициент площади
  */
  S_kr: ({ L_kr, B_kr, H_p1, H_p2, L_k, area_coeff }) => {
    const ac = area_coeff || 1;
    return roundToTenths(((L_kr + L_k) * H_p1 + B_kr * H_p2) * ac);
  },
  N_kon: ({ L_k, L_kr, H_p1 }) =>
    ceilToIntegers((L_k + 4 * sqrt(square(H_p1) + square((L_kr - L_k) / 2))) / 1.95),
  N_karn: ({ L_kr, B_kr }) => ceilToIntegers(2 * (B_kr + L_kr) / 1.95),

  NN_sa35: ({ S_kr }) => ceilToIntegers(6 * S_kr),
  NN_sa70: ({ L_kon, L_karn }) => ceilToIntegers(8 * (ceilToIntegers(L_kon / 2) + ceilToIntegers(L_karn / 2))),
};

// 3 Tented Шатровая кровля
export const tented: roof = {
  /*
  L_kr Ширина ската 1 (L) — длина карниза по длинной стороне
  B_kr Ширина ската 2 (M) — длина карниза по короткой стороне
  H_p1 Длина ската 1 (a) — длина ската от карниза длинной стороны до вершины
  H_p2 Длина ската 2 (a2) — длина ската от карниза короткой стороны до вершины
  area_coeff Коэффициент площади
  */

  S_kr: ({ L_kr, B_kr, H_p1, H_p2, area_coeff }) => {
    const ac = area_coeff || 1;
    return roundToTenths((L_kr * H_p1 + B_kr * H_p2) * ac);
  },

  N_kon: ({ L_kr, H_p1 }) =>
    ceilToIntegers(4 * sqrt(square(H_p1) + square(L_kr / 2)) / 1.95),
  N_karn: ({ L_kr, B_kr }) => ceilToIntegers(2 * (B_kr + L_kr) / 1.95),

  NN_sa35: ({ S_kr }) => ceilToIntegers(8 * S_kr),
  NN_sa70: ({ L_kon, L_karn }) => ceilToIntegers(12 * (ceilToIntegers(L_kon / 2) + ceilToIntegers(L_karn / 2))),
};

// 4 MultiPitch Многоскатная кровля
export const multipitch: roof = {
  /*
  B_kr2 Ширина фронтона 2
  B_kr Ширина фронтона
  L_kr Длина карниза
  L_k Длина конька
  H_p1 Высота подъема 1
  */
  S1: ({ B_kr2, L_k, H_p1 }) => roundToTenths(sqrt(square(B_kr2 / 2) + square(H_p1)) * L_k),
  S2: ({ B_kr, L_kr, H_p1 }) => roundToTenths(L_kr * sqrt(square(B_kr / 2) + square(H_p1))),
  S3: ({ B_kr, B_kr2, L_kr, H_p1 }) => roundToTenths(multipitch.S2({ B_kr, L_kr, H_p1 }) - (0.5 * B_kr2 * sqrt((square(B_kr / 2) + square(H_p1))))),

  S_kr: data => multipitch.S1(data) + multipitch.S2(data) + multipitch.S3(data),
  L_kon: ({ L_kr, L_k }) => roundToTenths(L_kr + L_k),
  L_karn: ({ L_kr, B_kr2 }) => roundToTenths(L_kr + (L_kr - B_kr2)),
  L_tor: ({ B_kr, H_p1 }) =>
    roundToTenths((4 * sqrt((square(B_kr / 2) + square(H_p1)))) + (2 * sqrt((square(B_kr / 2) + square(H_p1))))),
  L_end: ({ B_kr, B_kr2, H_p1 }) => roundToTenths(2 * sqrt(square(B_kr2 / 2) + square(B_kr / 2) + square(H_p1))),
};

// 5 Gable Двускатная кровля
export const gable: roof = {
  /*
  L_kr Длина карниза
  L_sk1 Длина ската 1
  L_sk2 Длина ската 2
  sheet_width Рабочая ширина листа
  area_coeff Коэффициент площади
  */

  S_kr: ({ L_kr, L_sk1, L_sk2, sheet_width, area_coeff }) => {
    const sw = sheet_width || 1;
    const ac = area_coeff || 1;
    const sheets = sheet_width ? ceilToIntegers(L_kr / sw) : ceilToIntegers(L_kr);
    return roundToTenths(sheets * (L_sk1 + L_sk2) * ac);
  },
  L_kon: ({ L_kr }) => L_kr,
  N_kon: ({ L_kr }) => ceilToIntegers(L_kr / 1.95),
  N_karn: ({ L_kr }) => ceilToIntegers(2 * L_kr / 1.95),
  N_tor: ({ L_sk1, L_sk2 }) => ceilToIntegers(2 * (L_sk1 + L_sk2) / 1.95),
};


// Константы для расчета материалов
export const F_kr = 1; // коэффициент квадратные метры в штуки либо 1
export const F_pl = 2; // длина планок для расчета кол-ва
export const F_sa = 1; // количество саморезов в упаковке либо 1

// Формулы расчета материалов
// export const N_kr = (S_kr: number) => ceilToIntegers((1.05 * S_kr) / F_kr); // 1. Количество кровли
export const N_kr = (S_kr: number) => ceilToHundredths((S_kr) / F_kr); // 1. Количество кровли

export const N_kon = (L_kon: number) => ceilToIntegers(L_kon / F_pl); // 2. Количество планок конька
export const N_karn = (L_karn: number) => ceilToIntegers(L_karn / F_pl); // 3. Количество планок карниза
export const N_tor = (L_tor: number) => ceilToIntegers(L_tor / F_pl); // 4. Количество торцевых планок
export const N_end = (L_end: number) => ceilToIntegers(L_end / F_pl); // 5. Количество планок ендов
// export const N_up = (L_kon: number, L_karn: number) => ceilToIntegers((L_karn + 2 * L_kon) / F_up); // 6. Количество упаковок уплотнителя
export const N_sa = (S_kr: number) => ceilToIntegers((8 * S_kr) / F_sa); // 7. Количество саморезов

export const N_sa35 = (S_kr: number) => ceilToIntegers((7 * S_kr) / F_sa); // 7.1. Количество саморезов 35
export const N_sa70 = (L_kon: number,  L_tor: number) => ceilToIntegers((6 * (L_kon + L_tor)) / F_sa); // 7.2. Количество саморезов 70

// Расчет материалов
export const materialsCalculations = ({
    S_kr,
    L_kon,
    L_end,

    NN_kon, // Кол-во планок конька (предрассчитано)
    NN_karn, // Кол-во планок карниза (предрассчитано)
    NN_tor, // Кол-во торцевых планок (предрассчитано)
    NN_sa35, // Саморезы 35 (или 0)
    NN_sa70, // Саморезы 70 (или 0)
  }: {
    S_kr: number;
    L_kon: number;
    L_end: number;
    NN_kon: number;
    NN_karn: number;
    NN_tor: number;
    NN_sa35: number;
    NN_sa70: number;
  }) => {
    return {
      N_kr: N_kr(S_kr),
      N_kon: NN_kon,
      N_karn: NN_karn,
      N_tor: NN_tor,
      N_end: N_end(L_end),
      N_sa: N_sa(S_kr),
      N_sa35: NN_sa35 ? NN_sa35 : N_sa35(S_kr),
      N_sa70: NN_sa70 ? NN_sa70 : N_sa70(L_kon, NN_tor),
    };
  };
